import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { merge, Observable, of, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { ConsumerPortalApiService } from '../../services/consumer-portal-api.service';
import { GenerateImageParams, GenerateImageResponse } from '../models/ai-image.model';

@Injectable({
    providedIn: 'root'
})
export class AiImageService {
    private readonly cpApi = inject(ConsumerPortalApiService);
    private readonly http = inject(HttpClient);

    // Per-request retries for transient upstream (5xx) failures, with exponential backoff.
    private static readonly maxImageRetries = 2;
    private static readonly retryBaseDelayMs = 800;

    generateImage(params: GenerateImageParams): Observable<GenerateImageResponse> {
        const formData = new FormData();
        formData.append('prompt', params.prompt);
        formData.append('generationType', params.generationType);
        if (params.targetWidth) {
            formData.append('targetWidth', params.targetWidth.toString());
        }
        if (params.targetHeight) {
            formData.append('targetHeight', params.targetHeight.toString());
        }
        return this.cpApi.post<GenerateImageResponse>({
            controller: 'ai-image/generate',
            body: formData,
            headers: {
                mimeType: 'multipart/form-data'
            },
            method: 'POST',
            camelCase: false
        });
    }

    /**
     * Fires `count` generation requests in parallel and emits exactly one item per request as it
     * settles: the {@link GenerateImageResponse} on success (images recovered by a retry simply
     * appear later), or `null` when a request finally fails. Each request retries transient 5xx
     * failures with exponential backoff before giving up. A single failed image never surfaces an
     * error or blocks the others — the `null` marker just lets callers retire its in-flight slot.
     * The stream completes when every request has settled.
     */
    generateImagesStream(params: GenerateImageParams, count = 4): Observable<GenerateImageResponse | null> {
        const requests = Array.from({ length: count }, () =>
            this.generateImage(params).pipe(
                retry({
                    count: AiImageService.maxImageRetries,
                    delay: (error, retryCount) => {
                        const status = this.errorStatus(error);
                        if (status !== undefined && status >= 500) {
                            return timer(AiImageService.retryBaseDelayMs * 2 ** (retryCount - 1));
                        }
                        // Non-transient (e.g. 4xx / content policy) — don't retry.
                        return throwError(() => error);
                    }
                }),
                catchError(() => of(null))
            )
        );
        return merge(...requests);
    }

    proxyImage(s3Url: string): Observable<Blob> {
        const url = `${this.cpApi.apiRoute}/ai-image/proxy`;
        const params = new HttpParams().set('url', s3Url);
        const headers = new HttpHeaders(this.cpApi.requestAuthHeaders);
        return this.http.get(url, { params, headers, responseType: 'blob' });
    }

    private errorStatus(error: unknown): number | undefined {
        const e = error as { status?: number; statusCode?: number; error?: { statusCode?: number } };
        return e?.status ?? e?.statusCode ?? e?.error?.statusCode;
    }
}
